if (!customElements.get('estoria-marquee')) {
  customElements.define('estoria-marquee', class EstoriaMarquee extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      this.viewport = this.querySelector('[data-marquee-viewport]');
      this.track = this.querySelector('[data-marquee-track]');
      this.group = this.querySelector('[data-marquee-group]');
      this.button = this.querySelector('[data-marquee-toggle]');
      if (!this.viewport || !this.track || !this.group) return;
      if (typeof window.matchMedia !== 'function' || typeof ResizeObserver === 'undefined') return;

      this.userPaused ??= false;
      if (!this.button) this.userPaused = false;
      this.active = true;
      this.events = new AbortController();
      const { signal } = this.events;
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.motionQuery.addEventListener('change', () => this.refresh(), { signal });
      this.button?.addEventListener('click', () => {
        if (this.dataset.animated !== 'true') return;
        this.userPaused = !this.userPaused;
        this.updatePause();
      }, { signal });

      this.observer = new ResizeObserver(() => {
        if (signal.aborted || this.frame !== undefined) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = undefined;
          if (!signal.aborted) this.refresh();
        });
      });
      this.observer.observe(this.viewport);
      this.observer.observe(this.group);
      this.refresh();
    }

    refresh() {
      if (!this.active) return;
      if (this.dataset.animate !== 'true' || this.motionQuery.matches || !this.group.children.length) {
        this.showStatic();
        return;
      }

      if (this.button) this.button.hidden = false;
      this.dataset.animated = 'true';
      this.style.setProperty('--marquee-min-width', `${this.viewport.clientWidth}px`);
      const distance = this.group.getBoundingClientRect().width;
      if (!this.viewport.clientWidth || !Number.isFinite(distance) || distance <= 0) {
        this.showStatic();
        return;
      }

      if (!this.duplicate) {
        this.duplicate = this.group.cloneNode(true);
        this.duplicate.removeAttribute('data-marquee-group');
        this.duplicate.setAttribute('data-marquee-duplicate', '');
        this.duplicate.setAttribute('aria-hidden', 'true');
        this.duplicate.setAttribute('inert', '');
        // Only the original words belong to the accessibility tree and theme editor.
        for (const element of [this.duplicate, ...this.duplicate.querySelectorAll('*')]) {
          for (const name of element.getAttributeNames()) {
            if (name === 'id' || name.startsWith('data-shopify-')) element.removeAttribute(name);
          }
        }
        this.track.append(this.duplicate);
      }

      const configuredSpeed = Number(this.dataset.speed);
      const speed = Number.isFinite(configuredSpeed) && configuredSpeed > 0 ? configuredSpeed : 60;
      this.style.setProperty('--marquee-distance', `${distance}px`);
      this.style.setProperty('--marquee-duration', `${distance / speed}s`);
      this.updatePause();
    }

    updatePause() {
      if (this.dataset.animated !== 'true') return;
      this.dataset.paused = String(this.userPaused);
      if (!this.button) return;
      const label = this.userPaused ? this.button.dataset.playLabel : this.button.dataset.pauseLabel;
      this.button.setAttribute('aria-label', label);
      this.button.setAttribute('title', label);
      this.button.querySelector('[data-play-icon]').hidden = !this.userPaused;
      this.button.querySelector('[data-pause-icon]').hidden = this.userPaused;
    }

    showStatic() {
      delete this.dataset.animated;
      delete this.dataset.paused;
      this.duplicate?.remove();
      this.duplicate = null;
      if (this.button) this.button.hidden = true;
      for (const property of ['--marquee-min-width', '--marquee-distance', '--marquee-duration']) {
        this.style.removeProperty(property);
      }
    }

    disconnectedCallback() {
      this.active = false;
      this.events?.abort();
      this.observer?.disconnect();
      if (this.frame !== undefined) cancelAnimationFrame(this.frame);
      this.frame = undefined;
      this.showStatic();
    }
  });
}
